import type { ProcessedMonthRow } from './dataParser'
import { getClientRiskLevel, type ClientRiskLevel } from './riskLevel'

export interface AdvancedFilterState {
	year: number | 'all'
	years: number[]
	months: number[]
	clientTier: string
	riskLevel: ClientRiskLevel | 'all'
	dateRange: { start: Date | null; end: Date | null }
}

/**
 * v1 applyAdvancedFilters — filter month rows and embedded client lists.
 */
export function applyAdvancedFilters(
	data: ProcessedMonthRow[],
	filters: AdvancedFilterState
): ProcessedMonthRow[] {
	if (!data || !Array.isArray(data)) {
		return []
	}

	let filteredData = [...data]

	const yearsSet =
		filters.years && filters.years.length > 0 ? new Set(filters.years) : null
	if (yearsSet && yearsSet.size > 0) {
		filteredData = filteredData.filter(item => yearsSet.has(Number(item.invoice_year)))
	} else if (filters.year !== 'all') {
		filteredData = filteredData.filter(
			item => Number(item.invoice_year) === Number(filters.year)
		)
	}

	if (filters.months && Array.isArray(filters.months) && filters.months.length > 0) {
		const monthSet = new Set(filters.months.map(m => Number(m)).filter(n => !Number.isNaN(n)))
		if (monthSet.size > 0) {
			filteredData = filteredData.filter(item =>
				monthSet.has(Number(item.invoice_month))
			)
		}
	}

	if (filters.dateRange.start && filters.dateRange.end) {
		filteredData = filteredData.filter(item => {
			const raw = item.invoice_month_start
			if (raw == null) return false
			const itemDate = new Date(String(raw))
			return (
				itemDate >= filters.dateRange.start! &&
				itemDate <= filters.dateRange.end!
			)
		})
	}

	filteredData = filteredData.map(month => {
		let clientDetails = Array.isArray(month.monthly_client_details)
			? month.monthly_client_details
			: []
		if (clientDetails.length === 0 && month.clients_with_overdue_details) {
			try {
				const parsed =
					typeof month.clients_with_overdue_details === 'string'
						? JSON.parse(month.clients_with_overdue_details)
						: month.clients_with_overdue_details
				clientDetails = Array.isArray(parsed) ? parsed : []
			} catch {
				clientDetails = []
			}
		}
		if (clientDetails.length === 0) {
			return {
				...month,
				monthly_client_details: [],
				clients_with_overdue_details: '[]',
			}
		}

		let filteredClients = clientDetails

		if (filters.clientTier !== 'all') {
			filteredClients = filteredClients.filter(
				client => String(client.client_tier) === filters.clientTier
			)
		}

		if (filters.riskLevel !== 'all') {
			filteredClients = filteredClients.filter(
				client => getClientRiskLevel(client) === filters.riskLevel
			)
		}

		return {
			...month,
			monthly_client_details: filteredClients,
			clients_with_overdue_details: JSON.stringify(filteredClients),
		}
	})

	return filteredData
}
